/* eslint-disable no-unused-vars */
import { useState, useEffect, useId, useRef } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Eye, EyeOff, ArrowUp, CloseForm, CheckForm } from 'assets/icons';
import AppnextHeaderLogo from 'assets/images/appnext_header_logo.svg';
import { Loader } from 'components';
import { GridBackground } from '../userLogin/GridBackground';
import { SubmitSuccessful } from './SubmitSuccessful';
import { authAPI } from 'services/newPlatformAuth.service';
import { showPolipop } from 'utils';
import AppnextLogo from 'assets/images/Appnext_Logo.png';
import * as S from './UserSignup.styled';

const captcha_sitekey = '5e592855-ea86-42d0-ab6b-b8daa7af0a19';
const useHcaptchaSignup = String(window.USE_HCAPTCHA_SIGNUP).toLowerCase() === 'true';

const passValidation = [
  { title: 'At least one upper case english letter', pattern: '^(?=.*[A-Z])' },
  { title: 'At least one lower case english letter', pattern: '^(?=.*[a-z])' },
  { title: 'At least one digit', pattern: '^(?=.*[0-9])' },
  { title: 'At least one special character', pattern: '[^A-Za-z0-9]' },
  { title: 'Minimum 8 in length', pattern: '^.{8,}$' },
];

const formatData = (data) => {
  const { passwordConfirm, ...dataToSend } = data;
  dataToSend.company = dataToSend.company.trim();
  return dataToSend;
};

const validatePassword = (value) => {
  for (const { title, pattern } of passValidation) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) return title;
  }
  return true;
};

const validateCompany = (value) => {
  return !!value.trim();
};

export const UserSignup = () => {
  const {
    handleSubmit,
    getValues,
    control,
    trigger,
    clearErrors,
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
  });
  const [userSignupAction, result] = authAPI.useUserSignupMutation();
  const formId = useId();
  const companyId = useId();
  const emailId = useId();
  const passId = useId();
  const passConfirmId = useId();
  const captchaWidgetIdRef = useRef(null);
  const submitSignupRef = useRef(null);
  const [captchaPending, setCaptchaPending] = useState(false);

  const resetCaptcha = () => {
    const widgetId = captchaWidgetIdRef.current;
    if (widgetId !== null && window.hcaptcha?.reset) {
      try {
        window.hcaptcha.reset(widgetId);
      } catch (e) {
        console.warn('hCaptcha reset failed', e);
      }
    }
  };

  const showCaptchaError = (message) => {
    showPolipop({ error: { data: { title: message } } });
  };

  const onSubmit = (data) => {
    userSignupAction(formatData(data));
  };

  const triggerCaptcha = () => {
    setCaptchaPending(true);
    const widgetId = captchaWidgetIdRef.current;
    if (widgetId === null || !window.hcaptcha || typeof window.hcaptcha.execute !== 'function') {
      showCaptchaError('Captcha is not available. Please refresh the page and try again.');
      setCaptchaPending(false);
      return;
    }
    try {
      window.hcaptcha.execute(widgetId);
    } catch (error) {
      console.error('hCaptcha execution failed:', error);
      showCaptchaError('Captcha is not available. Please refresh the page and try again.');
      setCaptchaPending(false);
    }
  };

  submitSignupRef.current = async (token) => {
    if (!token) {
      showCaptchaError('Captcha verification failed. Please try again.');
      setCaptchaPending(false);
      resetCaptcha();
      return;
    }
    try {
      await userSignupAction({
        ...formatData(getValues()),
        captchaToken: token,
      }).unwrap();
    } catch {
      // Server errors are shown via the result.isError effect.
    } finally {
      resetCaptcha();
      setCaptchaPending(false);
    }
  };

  const handleBlur = (field) => {
    trigger(field);
  };

  useEffect(() => {
    if (result.isError) {
      showPolipop(result);
    }
  }, [result]);

  useEffect(() => {
    if (!useHcaptchaSignup) return undefined;

    const captchaElement = document.getElementById('hcaptcha-signup');
    if (
      !captchaElement ||
      !window.hcaptcha ||
      typeof window.hcaptcha.render !== 'function' ||
      captchaWidgetIdRef.current !== null
    ) {
      return undefined;
    }

    const widgetId = window.hcaptcha.render(captchaElement, {
      sitekey: captcha_sitekey,
      size: 'invisible',
      callback: (token) => {
        submitSignupRef.current?.(token);
      },
      'error-callback': () => {
        console.error('Captcha error occurred');
        const id = captchaWidgetIdRef.current;
        if (id !== null && window.hcaptcha?.reset) {
          try {
            window.hcaptcha.reset(id);
          } catch (e) {
            console.warn('hCaptcha reset failed', e);
          }
        }
        setCaptchaPending(false);
        showPolipop({
          error: { data: { title: 'Captcha verification failed. Please try again.' } },
        });
      },
      'expired-callback': () => {
        console.warn('Captcha expired');
        const id = captchaWidgetIdRef.current;
        if (id !== null && window.hcaptcha?.reset) {
          try {
            window.hcaptcha.reset(id);
          } catch (e) {
            console.warn('hCaptcha reset failed', e);
          }
        }
        setCaptchaPending(false);
        showPolipop({ error: { data: { title: 'Captcha expired. Please try again.' } } });
      },
    });
    captchaWidgetIdRef.current = widgetId;

    return () => {
      const id = captchaWidgetIdRef.current;
      if (id !== null && window.hcaptcha?.remove) {
        try {
          window.hcaptcha.remove(id);
        } catch (e) {
          console.warn('hCaptcha remove failed', e);
        }
      }
      captchaWidgetIdRef.current = null;
    };
  }, []);

  const isSignupSuccessful =
    isSubmitSuccessful && !result.isError && !result.isLoading && result.status !== 'uninitialized';

  const type = useWatch({ control, name: 'type', defaultValue: 'publisher' });
  const termsLink =
    type === 'advertiser'
      ? 'https://appnext.com/terms-conditions-advertisers'
      : 'https://appnext.com/terms-conditions-publishers';

  return (
    <S.Box>
      <GridBackground variant='signup' />
      {isSignupSuccessful ? (
        <SubmitSuccessful data={formatData(getValues())} />
      ) : (
        <>
          {(isSubmitting || result.isLoading || captchaPending) && (
            <S.LoaderBox>
              <Loader.Local delay={100} />
            </S.LoaderBox>
          )}
          <S.StyledForm
            id={formId}
            onSubmit={handleSubmit(useHcaptchaSignup ? triggerCaptcha : onSubmit)}
          >
            <S.FlexCenter>
              <S.Label>Sign Up</S.Label>
              <img src={AppnextLogo} alt='Appnext Logo' height={33} />
            </S.FlexCenter>
            <S.Separator />
            <S.RadioTitle>You Are:</S.RadioTitle>

            <Controller
              name='type'
              control={control}
              defaultValue='advertiser'
              render={(props) => {
                const { field } = props;
                return (
                  <S.RadioBoxGroup>
                    <S.RadioBtn
                      type='button'
                      active={field.value === 'advertiser'}
                      onClick={() => field.onChange('advertiser')}
                    >
                      Advertiser
                    </S.RadioBtn>
                    <S.RadioBtn
                      type='button'
                      active={field.value === 'publisher'}
                      onClick={() => field.onChange('publisher')}
                    >
                      Publisher
                    </S.RadioBtn>
                  </S.RadioBoxGroup>
                );
              }}
            />

            <Controller
              name='company'
              control={control}
              rules={{
                required: 'Company name is required',
                validate: validateCompany,
              }}
              render={(props) => {
                const { field, formState } = props;
                const errors = formState.errors;
                return (
                  <S.InputGroup>
                    <S.InputLabel htmlFor={companyId}>Company:</S.InputLabel>
                    <S.InputWrapper error={Boolean(errors.company)}>
                      <S.StyledInput
                        id={companyId}
                        placeholder='Your company name'
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onFocus={() => {
                          clearErrors('company');
                        }}
                        onBlur={() => handleBlur('company')}
                      />
                    </S.InputWrapper>
                    {errors?.company && <S.ErrorText>{errors.company.message}</S.ErrorText>}
                  </S.InputGroup>
                );
              }}
            />

            <Controller
              name='username'
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Please enter a valid email address',
                },
              }}
              render={(props) => {
                const { field, formState } = props;
                const errors = formState.errors;
                return (
                  <S.InputGroup>
                    <S.InputLabel htmlFor={emailId}>Email:</S.InputLabel>
                    <S.InputWrapper error={Boolean(errors.username)}>
                      <S.StyledInput
                        id={emailId}
                        placeholder='your@email.com'
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onFocus={() => {
                          clearErrors('username');
                        }}
                        onBlur={() => handleBlur('username')}
                      />
                    </S.InputWrapper>
                    {errors?.username && <S.ErrorText>{errors.username.message}</S.ErrorText>}
                  </S.InputGroup>
                );
              }}
            />

            <Controller
              name='password'
              control={control}
              rules={{
                required: 'Password is required',
                validate: validatePassword,
              }}
              render={(props) => {
                const { field, formState } = props;
                const errors = formState.errors;
                const [showPassword, setShowPassword] = useState(false);
                return (
                  <S.InputGroup>
                    <S.InputLabel htmlFor={passId}>Password:</S.InputLabel>
                    <S.InputWrapper error={Boolean(errors.password)}>
                      <S.StyledInput
                        id={passId}
                        type={showPassword ? 'text' : 'password'}
                        placeholder='Create a password'
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onFocus={() => {
                          clearErrors('password');
                        }}
                        onBlur={() => handleBlur('password')}
                      />
                      <S.InputIcon onClick={() => setShowPassword((prev) => !prev)}>
                        {showPassword ? <EyeOff /> : <Eye />}
                      </S.InputIcon>
                    </S.InputWrapper>
                    {errors?.password && <S.ErrorText>{errors.password.message}</S.ErrorText>}
                    <S.PassValidationBox>
                      {passValidation.map((item) => {
                        const pattern = new RegExp(item.pattern);
                        const isNotEmpty = field.value?.length > 0;
                        const isComplete = pattern.test(field.value);
                        return (
                          <S.ValidationItem
                            isRed={isNotEmpty && !isComplete}
                            isGreen={isNotEmpty && isComplete}
                            key={item.title}
                          >
                            {isNotEmpty && !isComplete && <CloseForm />}
                            {isNotEmpty && isComplete && <CheckForm />}
                            {!isNotEmpty && (
                              <S.IconWrap>
                                <ArrowUp />
                              </S.IconWrap>
                            )}
                            {item.title}
                          </S.ValidationItem>
                        );
                      })}
                    </S.PassValidationBox>
                  </S.InputGroup>
                );
              }}
            />

            <Controller
              name='passwordConfirm'
              control={control}
              rules={{
                required: 'Retype Password is required',
                validate: (value) => {
                  if (!value) return 'Retype Password is required';
                  const { password } = getValues();
                  return value === password || 'Passwords do not match';
                },
              }}
              render={(props) => {
                const { field, formState } = props;
                const errors = formState.errors;
                const [showPassword, setShowPassword] = useState(false);
                return (
                  <S.InputGroup>
                    <S.InputLabel htmlFor={passConfirmId}>Retype Password:</S.InputLabel>
                    <S.InputWrapper error={Boolean(errors.passwordConfirm)}>
                      <S.StyledInput
                        id={passConfirmId}
                        type={showPassword ? 'text' : 'password'}
                        placeholder='Repeat your password'
                        name={field.name}
                        value={field.value || ''}
                        onChange={field.onChange}
                        onFocus={() => {
                          clearErrors('passwordConfirm');
                        }}
                        onBlur={() => handleBlur('passwordConfirm')}
                      />
                      <S.InputIcon onClick={() => setShowPassword((prev) => !prev)}>
                        {showPassword ? <EyeOff /> : <Eye />}
                      </S.InputIcon>
                    </S.InputWrapper>
                    {errors?.passwordConfirm && (
                      <S.ErrorText>{errors.passwordConfirm.message}</S.ErrorText>
                    )}
                  </S.InputGroup>
                );
              }}
            />

            <S.Paragraph>
              By creating an account, you agree to our{' '}
              <S.OuterLink href={termsLink} target='_blank' rel='noopener nofollow'>
                Terms of Service
              </S.OuterLink>{' '}
              (General, Advertisers, Publishers)
            </S.Paragraph>

            <S.SignUpButton
              type='submit'
              disabled={!isValid || isSubmitting || result.isLoading || captchaPending}
            >
              Sign Up
            </S.SignUpButton>

            <S.Separator />
            <S.Footer>
              Already have an account? <S.StyledLink to='/userLogin'>Log in</S.StyledLink>
            </S.Footer>
          </S.StyledForm>
        </>
      )}
      {useHcaptchaSignup && <div id='hcaptcha-signup' className='h-captcha' />}
    </S.Box>
  );
};
